// src/pages/BulkUploadPreviewPage.jsx
import { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  generatePath,
} from "react-router-dom";
import Header from "../components/common/Header";
import http from "../api/server";
import { UPLOAD_EXCEL_API } from "../api/apiPath";

// --- helpers to map error text -> cell highlights ---
const nameMap = {
  firstname: "firstName",
  middlename: "middleName",
  lastname: "lastName",
  email: "email",
  phonenumber: "phoneNumber",
  propertyname: "propertyName",
  propertyaddress: "propertyAddress",
  apartmentorhouseno: "apartmentNo",
  city: "city",
  postcode: "postCode",
  secondaryaddress: "secondaryAddress",
};
const norm = (s) => (s || "").replace(/\s+/g, "").toLowerCase();

const INVALID_CELL_STYLE = {
  outline: "2px solid #dc3545",
  background: "#fff5f5",
};
const tdBase = (w) => ({
  maxWidth: w ?? 120,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "middle",
});

function parseErrorToFlags(errorMessage) {
  const flags = {};
  if (!errorMessage) return flags;
  const parts = errorMessage
    .split(";")
    .map((p) => p.trim())
    .filter(Boolean);

  for (const part of parts) {
    if (/^missing required:/i.test(part)) {
      const afterColon = part.split(":")[1] || "";
      afterColon
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach((raw) => {
          const key = nameMap[norm(raw)];
          if (key) flags[key] = "required";
        });
    } else if (/invalid uk post code/i.test(part)) {
      flags.postCode = "invalid";
    } else if (/invalid email/i.test(part)) {
      flags.email = "invalid";
    } else if (/invalid phone number/i.test(part)) {
      flags.phoneNumber = "invalid";
    }
  }
  return flags;
}

const renderCell = (value, badKind, width, rowBg) => (
  <td
    style={{
      ...(badKind ? INVALID_CELL_STYLE : {}),
      ...tdBase(width),
      backgroundColor: rowBg, // <-- ensure zebra shows
    }}
    title={value ?? ""}
  >
    <span>{value ?? ""}</span>
    {badKind && (
      <div className="text-danger small" style={{ lineHeight: 1.1 }}>
        {badKind === "required" ? "Required" : "Invalid"}
      </div>
    )}
  </td>
);

export default function BulkUploadPreviewPage() {
  const { state } = useLocation(); // { preview, file }
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const { clientName } = useParams();

  const [preview, setPreview] = useState(
    () =>
      state?.preview || {
        totalRows: 0,
        validRows: 0,
        invalidRows: 0,
        inserted: 0,
        validRecords: [],
        invalidRecords: [],
        summaryMessage: "",
      }
  );
  const file = state?.file;

  useEffect(() => {
    // if (!state?.preview) navigate("/bulk-upload", { replace: true });
    if (!state?.preview) {
      navigate(
        generatePath("/client/:clientName/bulk-upload", { clientName }),
        { replace: true }
      );
    }
  }, [state, navigate]);

  // --- merge valid + invalid to one table ---
  const mergedRows = useMemo(() => {
    const v = (preview.validRecords || []).map((r) => ({
      ...r,
      __status: "Valid",
      errorMessage: "",
    }));
    const i = (preview.invalidRecords || []).map((r) => ({
      ...r,
      __status: "Invalid",
    }));
    return [...v, ...i].sort((a, b) => (a.rowNumber ?? 0) - (b.rowNumber ?? 0));
  }, [preview]);

  // counts (for the chips)
  const validCount = preview.validRecords?.length ?? 0;
  const invalidCount = preview.invalidRecords?.length ?? 0;

  const [statusFilter, setStatusFilter] = useState("all");
  // rows after applying the status filter
  const filteredRows = useMemo(() => {
    if (statusFilter === "valid")
      return mergedRows.filter((r) => r.__status === "Valid");
    if (statusFilter === "invalid")
      return mergedRows.filter((r) => r.__status !== "Valid");
    return mergedRows;
  }, [mergedRows, statusFilter]);

  // --- client-side pagination ---
  const [page, setPage] = useState(1);

  const pageSize = 5;
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const pageRows = filteredRows.slice(start, end);

  useEffect(() => {
    // keep page within bounds if data changes
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const proceedToUpload = async () => {
    if (!file) return;
    setBusy(true);
    setUploadErr("");

    try {
      const fd = new FormData();
      // fd.append("file", file);
      fd.append("file", file, file.name);

      const { data } = await http.post(`${UPLOAD_EXCEL_API}?isSave=true`, fd, {
        // remove any default JSON content-type so the browser sets the multipart boundary
        transformRequest: [
          (d, headers) => {
            delete headers["Content-Type"];
            if (headers.common) delete headers.common["Content-Type"];
            if (headers.post) delete headers.post["Content-Type"];
            return d;
          },
        ],
      });

      setPreview({
        totalRows: data?.totalRows ?? 0,
        validRows: data?.validRows ?? 0,
        invalidRows: data?.invalidRows ?? data?.invalidRecords?.length ?? 0,
        inserted: data?.inserted ?? 0,
        validRecords: data?.validRecords || [],
        invalidRecords: data?.invalidRecords || [],
        summaryMessage: data?.summaryMessage || "",
      });
      setPage(1);

      const inserted = data?.inserted ?? 0;
      const skipped = data?.invalidRows ?? data?.invalidRecords?.length ?? 0;
      const msg =
        (data?.summaryMessage?.includes("\n")
          ? data.summaryMessage
          : data?.summaryMessage?.replace(/\s*❌/, "\n❌")) ||
        `✅ ${inserted} residents uploaded successfully.\n❌ ${skipped} records skipped due to errors.`;
      setResultMessage(msg);
      setShowResultModal(true);
    } catch (e) {
      setUploadErr(e?.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  // csv download (unchanged)
  const csvEscape = (v) => {
    const s = `${v ?? ""}`;
    return /[",\n\r]/.test(s) ? `"${s.replaceAll(`"`, `""`)}"` : s;
  };
  const downloadInvalidCsv = () => {
    if (!preview.invalidRecords?.length) return;
    const headers = [
      "Row #",
      "First Name",
      "Middle Name",
      "Last Name",
      "Email",
      "Phone Number",
      "Property Name",
      "Property Address",
      "Apartment No.",
      "City",
      "Post Code",
      "Secondary Address",
      "Resident Category",
      "Error",
    ];
    const lines = [headers.join(",")];

    preview.invalidRecords.forEach((r, idx) => {
      const d = r.residentDetails || r.ResidentDetails || {};
      const rowNum = r.rowNumber ?? r.RowNumber ?? idx + 2;
      const errMsg = r.errorMessage ?? r.ErrorMessage ?? "";
      const vals = [
        rowNum,
        d.firstName ?? d.FirstName ?? "",
        d.middleName ?? d.MiddleName ?? "",
        d.lastName ?? d.LastName ?? "",
        d.email ?? d.Email ?? "",
        d.phoneNumber ?? d.PhoneNumber ?? "",
        d.propertyName ?? d.PropertyName ?? "",
        d.propertyAddress ?? d.propertyAddress ?? "",
        d.apartmentNo ?? d.ApartmentNo ?? "",
        d.city ?? d.City ?? "",
        d.postCode ?? d.PostCode ?? "",
        d.secondaryAddress ?? d.SecondaryAddress ?? "",
        errMsg,
      ].map(csvEscape);
      lines.push(vals.join(","));
    });

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Invalid_Records_${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- table styles (to match screenshot structure) ---
  const cardStyle = {
    border: "1px solid #F0B429", // thin warm border like the mock highlight
    boxShadow: "0 8px 16px rgba(16,24,40,.06)",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
  };
  const wrapStyle = {
    maxHeight: "58vh",
    overflow: "auto",
  };
  const thStyle = {
    background: "#F6F8FC",
    color: "#22304A",
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: ".02em",
    borderBottom: "1px solid #E8EDF5",
    position: "sticky",
    top: 0,
    zIndex: 1,
  };
  const rowStyle = {
    borderBottom: "1px solid #EEF2F7",
  };
  const zebraBg = (absIndex) => (absIndex % 2 === 0 ? "#FFFFFF" : "#F6F9FF");
  const pill = (text, tone) => {
    const map = {
      valid: { bg: "#EAF7EE", fg: "#1B7F3A" },
      invalid: { bg: "#FFF3E6", fg: "#C35500" },
    };
    const c = map[tone];
    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 600,
          background: c.bg,
          color: c.fg,
        }}
      >
        {text}
      </span>
    );
  };

  function StatusChip({ active, onClick, children }) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="btn btn-sm"
        style={{
          borderRadius: 16,
          padding: "6px 12px",
          background: active ? "#0F1A2A" : "#fff",
          color: active ? "#fff" : "#0F1A2A",
          border: "1px solid #E5E7EB",
          boxShadow: active ? "0 1px 2px rgba(16,24,40,.08)" : "none",
        }}
      >
        {children}
      </button>
    );
  }

  // simple numbered pagination w/ prev/next
  const Pagination = () => {
    //if (pageCount <= 1) return null;

    // build compact list like: Prev | 1 [2] 3 ... 8 | Next
    const nums = [];
    const add = (n) =>
      nums.push(
        <button
          key={n}
          onClick={() => setPage(n)}
          className="btn btn-sm"
          style={{
            minWidth: 34,
            height: 34,
            borderRadius: 6,
            border: "1px solid #E5E7EB",
            background: n === page ? "#0F1A2A" : "#fff",
            color: n === page ? "#fff" : "#0F1A2A",
            marginLeft: 6,
          }}
        >
          {n}
        </button>
      );

    const show = new Set([1, page, page - 1, page + 1, pageCount]);
    for (let n = 1; n <= pageCount; n++) {
      if (
        show.has(n) ||
        (n === 2 && page <= 3) ||
        (n === pageCount - 1 && page >= pageCount - 2)
      ) {
        add(n);
      } else if (
        (n === 3 && page > 3) ||
        (n === pageCount - 2 && page < pageCount - 2)
      ) {
        nums.push(
          <span key={`el-${n}`} style={{ marginLeft: 6, color: "#6B7280" }}>
            …
          </span>
        );
      }
    }

    return (
      <div className="d-flex align-items-center justify-content-end mt-3">
        <button
          className="btn btn-sm"
          disabled={page === 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          style={{
            border: "1px solid #E5E7EB",
            background: "#fff",
            color: "#374151",
            borderRadius: 6,
            padding: "6px 10px",
            marginRight: 8,
          }}
        >
          Previous
        </button>
        {nums}
        <button
          className="btn btn-sm"
          disabled={page === pageCount}
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          style={{
            border: "1px solid #E5E7EB",
            background: "#fff",
            color: "#374151",
            borderRadius: 6,
            padding: "6px 10px",
            marginLeft: 8,
          }}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <>
      <style>{`
  /* Mobile buttons */
  @media (max-width: 576px) {
    .bup-actions {
      flex-direction: column;
      align-items: stretch !important;
      gap: 10px !important;
    }
    .bup-actions .btn {
      width: 100%;
      height: 48px;
      white-space: normal;   /* allow "Proceed to Upload" to wrap if needed */
      border-radius: 10px;
      justify-content: center;
    }
  }
    /* Mobile buttons */
  @media (max-width: 576px) {
    .bup-actions {
      flex-direction: column;
      align-items: stretch !important;
      gap: 10px !important;
    }
    .bup-actions .btn { width: 100%; height: 48px; white-space: normal; border-radius: 10px; justify-content: center; }
  }

  /* Result modal */
  .bup-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(15,26,42,.55);
    backdrop-filter: blur(2px);
    display: flex; align-items: center; justify-content: center;
    z-index: 2000;
  }
  .bup-modal {
    width: 560px; max-width: 92vw;
    background: #fff; border-radius: 16px;
    box-shadow: 0 24px 48px rgba(16,24,40,.18);
    padding: 28px 24px; text-align: center;
  }
  .bup-modal .check {
    width: 60px; height: 60px; border-radius: 50%;
    border: 3px solid #22c55e; display:flex; align-items:center; justify-content:center;
    margin: 4px auto 14px auto;
  }
  .bup-modal h5 { margin: 0 0 6px 0; color: #0F1A2A; font-weight: 700; }
  .bup-modal .summary { color: #111827; font-size: 16px;  white-space: pre-line; }
  .bup-modal-actions { margin-top: 18px; display:flex; gap:10px; justify-content:center; }
  @media (max-width: 576px) {
    .bup-modal { padding: 22px 18px; border-radius: 14px; }
    .bup-modal-actions { flex-direction: column; }
    .bup-modal-actions .btn { width: 100%; height: 44px; }
  }
`}</style>

      <Header />

      <div className="dashboard_body_main" style={{ background: "#F5F7FA" }}>
        <div className="d-flex dashboard_body flex-column py-4 px-5 w-100 position-relative">
          {busy && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <div className="d-flex flex-column align-items-center">
                <div className="spinner-border" role="status" />
                <div className="mt-2 small text-muted">
                  Uploading valid rows…
                </div>
              </div>
            </div>
          )}

          <h2 className="mb-1" style={{ color: "#0F1A2A", fontWeight: 700 }}>
            Upload Preview
          </h2>

          {preview.summaryMessage && (
            <div className="alert alert-info py-2 mt-2">
              {preview.summaryMessage}
            </div>
          )}
          {uploadErr && (
            <div className="text-danger small mb-2">{uploadErr}</div>
          )}

          {/* counters like chips */}
          <div className="d-flex flex-wrap gap-2 my-2 align-items-center">
            <StatusChip
              active={statusFilter === "all"}
              onClick={() => {
                setStatusFilter("all");
                setPage(1);
              }}
            >
              All{" "}
              <span className="ms-1 badge bg-light text-dark">
                {mergedRows.length}
              </span>
            </StatusChip>

            <StatusChip
              active={statusFilter === "valid"}
              onClick={() => {
                setStatusFilter("valid");
                setPage(1);
              }}
            >
              Valid <span className="ms-1 badge bg-success">{validCount}</span>
            </StatusChip>

            <StatusChip
              active={statusFilter === "invalid"}
              onClick={() => {
                setStatusFilter("invalid");
                setPage(1);
              }}
            >
              Invalid{" "}
              <span className="ms-1 badge bg-danger">{invalidCount}</span>
            </StatusChip>

            <span className="ms-2 small text-muted">
              Inserted: {preview.inserted}
            </span>
          </div>

          {/* TABLE CARD */}
          <div style={cardStyle} className="mt-2">
            <div style={wrapStyle}>
              <table className="table mb-0" style={{ fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 80 }}>ROW #</th>
                    <th style={{ ...thStyle, width: 110 }}>STATUS</th>
                    <th style={{ ...thStyle, minWidth: 100 }}>FIRST NAME</th>
                    {/* <th style={{ ...thStyle, minWidth: 100 }}>MIDDLE NAME</th> */}
                    <th style={{ ...thStyle, minWidth: 100 }}>LAST NAME</th>
                    <th style={{ ...thStyle, minWidth: 180 }}>EMAIL</th>
                    <th style={{ ...thStyle, minWidth: 130 }}>PHONE NUMBER</th>
                    <th style={{ ...thStyle, minWidth: 160 }}>PROPERTY NAME</th>
                    <th style={{ ...thStyle, minWidth: 220 }}>
                      PROPERTY ADDRESS
                    </th>
                    <th style={{ ...thStyle, minWidth: 180 }}>
                      Apartment/Flat/House No
                    </th>
                    <th style={{ ...thStyle, minWidth: 130 }}>CITY</th>
                    <th style={{ ...thStyle, minWidth: 120 }}>POST CODE</th>
                    <th style={{ ...thStyle, minWidth: 200 }}>SECONDARY</th>
                    <th style={{ ...thStyle, minWidth: 200 }}>RESIDENT CATEGORY</th>
                    <th style={{ ...thStyle, minWidth: 260 }}>
                      VALIDATION REASON
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pageRows.map((r, idx) => {
                    const d = r.residentDetails || r.ResidentDetails || {};
                    const rowNum =
                      r.rowNumber ?? r.RowNumber ?? start + idx + 2;
                    const status = r.__status === "Valid" ? "Valid" : "Invalid";
                    const errMsg = r.errorMessage ?? r.ErrorMessage ?? "";
                    const flags =
                      status === "Invalid" ? parseErrorToFlags(errMsg) : {};
                    const rowBg = zebraBg(start + idx); // <-- stripe color here

                    return (
                      <tr key={`${status}-${start + idx}`} style={rowStyle}>
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            backgroundColor: rowBg,
                          }}
                        >
                          {rowNum}
                        </td>
                        <td style={{ backgroundColor: rowBg }}>
                          {status === "Valid"
                            ? pill("Valid", "valid")
                            : pill("Invalid", "invalid")}
                        </td>

                        {renderCell(
                          d.firstName ?? d.FirstName,
                          flags.firstName,
                          120,
                          rowBg
                        )}
                        {/* {renderCell(
                          d.middleName ?? d.MiddleName,
                          flags.middleName,
                          100,
                          rowBg
                        )} */}
                        {renderCell(
                          d.lastName ?? d.LastName,
                          flags.lastName,
                          120,
                          rowBg
                        )}
                        {renderCell(
                          d.email ?? d.Email,
                          flags.email,
                          180,
                          rowBg
                        )}
                        {renderCell(
                          d.phoneNumber ?? d.PhoneNumber,
                          flags.phoneNumber,
                          130,
                          rowBg
                        )}
                        {renderCell(
                          d.propertyName ?? d.PropertyName,
                          flags.propertyName,
                          150,
                          rowBg
                        )}
                        {renderCell(
                          d.propertyAddress ?? d.propertyAddress,
                          flags.propertyAddress,
                          200,
                          rowBg
                        )}
                        {renderCell(
                          d.apartmentNo ?? d.ApartmentNo,
                          flags.apartmentNo,
                          120,
                          rowBg
                        )}
                        {renderCell(d.city ?? d.City, flags.city, 120, rowBg)}
                        {renderCell(
                          d.postCode ?? d.PostCode,
                          flags.postCode,
                          110,
                          rowBg
                        )}
                        {renderCell(
                          d.secondaryAddress ?? d.SecondaryAddress,
                          flags.secondaryAddress,
                          200,
                          rowBg
                        )}
                        {renderCell(
                          d.category ?? d.category,
                          flags.category,
                          200,
                          rowBg
                        )}
                        <td
                          style={{
                            minWidth: 260,
                            whiteSpace: "pre-wrap",
                            backgroundColor: rowBg,
                          }}
                          title={errMsg}
                        >
                          {status === "Invalid" ? errMsg : ""}
                        </td>
                      </tr>
                    );
                  })}
                  {/* ...rest unchanged */}
                </tbody>
              </table>
            </div>

            {/* footer: showing range + pagination */}
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ background: "#fff" }}
            >
              <div className="text-muted" style={{ fontSize: 12 }}>
                {filteredRows.length > 0
                  ? `Showing ${start + 1} to ${Math.min(
                      end,
                      filteredRows.length
                    )} of ${filteredRows.length} results`
                  : "Showing 0 results"}
              </div>

              <Pagination />
            </div>
          </div>

          {/* actions bar */}
          <div className="bup-actions d-flex justify-content-end gap-2 mt-3">
            <button
              className="btn btn-secondary"
              onClick={() => navigate("../bulk-upload", { relative: "path" })}
              disabled={busy}
            >
              Back
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={downloadInvalidCsv}
              disabled={!preview.invalidRecords?.length || busy}
            >
              Download Invalid CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={proceedToUpload}
              disabled={!file || !preview.validRecords?.length || busy}
            >
              {busy ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Uploading…
                </>
              ) : (
                "Proceed to Upload"
              )}
            </button>
          </div>
        </div>
        {showResultModal && (
          <div
            className="bup-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bup-modal-title"
          >
            <div className="bup-modal">
              <div className="check" aria-hidden="true">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="#22c55e"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h5 id="bup-modal-title">Data Uploaded Successfully !</h5>
              <div className="summary">{resultMessage}</div>

              <div className="bup-modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowResultModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
